/**
 * #342 (owner 2026-10-14) — the look of a camera's coverage wedge on the fleet map, in one place. On satellite
 * imagery the white halo of item 330 vanished over road markings and pale concrete and the coloured edge sank into
 * dark roofs, so the wedge is three outlines: a dark casing under the white halo under the coloured wedge, which reads
 * on light and dark ground alike. From zoom 17, where the group circles end, the lines and the fill are bolder; at
 * 15-16 (fleet zoom) they stay as they were, so clustered depot cameras do not blur into one blob. The camera the user
 * is looking at (its pin hovered, or else the selected camera) has its wedge raised, with a static direction tick past
 * its pin. Nothing animates. Pure: plain google.maps option objects, no React.
 */
import { PIN_SIZE_WIDTH } from '@/lib/mapSettings'
import { ACTIVE_PIN_SCALE, ALERT_PIN_SCALE, SHAPES } from '@/lib/pinGeometry'
import type { PinShape, PinSize } from '@/types'

/** From this zoom the wedges are drawn bolder (the group circles break up at 17: CLUSTER_MAX_ZOOM is 16). */
export const WEDGE_BOLD_ZOOM = 17
/** slate-900, the dark already used for the pin's edge on satellite fields (#306). */
const CASING_COLOUR = '#0f172a'
const HALO_COLOUR = '#ffffff'

/** 'fleet' below WEDGE_BOLD_ZOOM (the wedges show from 15), 'bold' from it. */
export type WedgeBand = 'fleet' | 'bold'

export function wedgeBand(zoom: number): WedgeBand {
  return zoom >= WEDGE_BOLD_ZOOM ? 'bold' : 'fleet'
}

/** The CP 342 table: the stroke widths in px and the fill in % of each band; the opacities do not change by band. */
const BANDS: Record<WedgeBand, { casing: number; halo: number; edge: number; fill: number }> = {
  fleet: { casing: 9, halo: 6, edge: 3, fill: 35 },
  bold: { casing: 10, halo: 7, edge: 4, fill: 45 },
}
const CASING_OPACITY = 0.7
const HALO_OPACITY = 0.75
const EDGE_OPACITY = 0.9
/** A raised wedge, in either band: every outline 1 px wider, the edge fully opaque, the fill 15 points stronger, and
 *  its three polygons 10 higher so it sits above its neighbours. */
const RAISED = { widen: 1, edgeOpacity: 1, fill: 15, zIndex: 10 }

export interface WedgeOutline { strokeColor: string; strokeOpacity: number; strokeWeight: number; fillOpacity: number; zIndex: number }
export interface WedgeFill extends WedgeOutline { fillColor: string }
/** The options of a wedge's three polygons, bottom to top. */
export interface WedgeStyles { casing: WedgeOutline; halo: WedgeOutline; main: WedgeFill }

/** The three polygons' look for a band, raised (`active`: its pin hovered or its camera selected) or not, with the
 *  edge and the fill in `colour` (the wedge colour setting). The casing and the halo never take the colour. */
export function wedgeStyles(band: WedgeBand, active: boolean, colour: string): WedgeStyles {
  const b = BANDS[band]
  const widen = active ? RAISED.widen : 0
  const z = active ? RAISED.zIndex : 0
  return {
    casing: { strokeColor: CASING_COLOUR, strokeOpacity: CASING_OPACITY, strokeWeight: b.casing + widen, fillOpacity: 0, zIndex: z },
    halo: { strokeColor: HALO_COLOUR, strokeOpacity: HALO_OPACITY, strokeWeight: b.halo + widen, fillOpacity: 0, zIndex: z + 1 },
    main: {
      strokeColor: colour,
      strokeOpacity: active ? RAISED.edgeOpacity : EDGE_OPACITY,
      strokeWeight: b.edge + widen,
      fillColor: colour,
      fillOpacity: (b.fill + (active ? RAISED.fill : 0)) / 100,
      zIndex: z + 2,
    },
  }
}

/** How far the tick reaches past the active pin's outline, in px. */
const TICK_PAST_PIN_PX = 12

/** The tick's length on screen, from the apex along the heading: to the active pin's outline that way, then 12 px on
 *  (controller ruling 2026-10-14). The pin stands on its tip, the apex, with its head above it, so a tick pointing
 *  north must clear the whole pin and one pointing south only the tip. The pin is taken as its head circle (per shape
 *  and size, grown from the tip by ACTIVE_PIN_SCALE, or by ALERT_PIN_SCALE for an `alerting` pin, which is drawn at
 *  the alert scale even while active). For a bearing θ on screen (0 = up; the caller takes off the map's rotation), a
 *  head centre h px above the tip and a radius r px: h cos θ + sqrt(r² - h² sin² θ) where the ray meets the head;
 *  where it misses, the head's closest approach along the ray, h cos θ (final review: near the tangent the pin's
 *  point, with its outline, is wider than the circle, so a ray that misses the circle can still run inside the pin).
 *  Never less than 0, then 12 px on. The pins are drawn above every shape, so the part under the pin is hidden. */
export function tickLengthPx(pinShape: PinShape, pinSize: PinSize, headingDeg: number, alerting = false): number {
  const spec = SHAPES[pinShape]
  const pxPerUnit = (PIN_SIZE_WIDTH[pinSize] / spec.w) * (alerting ? ALERT_PIN_SCALE : ACTIVE_PIN_SCALE)
  const h = (spec.h - spec.head.cy) * pxPerUnit
  const r = spec.head.r * pxPerUnit
  const theta = (headingDeg * Math.PI) / 180
  const across = h * Math.sin(theta)
  const reach = h * Math.cos(theta) + Math.sqrt(Math.max(r * r - across * across, 0))
  return Math.max(reach, 0) + TICK_PAST_PIN_PX
}

/** A closed arrowhead pointing along the line (a symbol's -y runs along a polyline), FORWARD_CLOSED_ARROW's shape as
 *  a plain path. */
const ARROWHEAD = 'M -2,2 0,-2 2,2 z'
/** The arrowhead's 1 px rim, the casing's dark: it reads on the wedge's own colour and keeps a white tick (the white
 *  wedge colour, or a white pin's) visible on pale ground. */
const TICK_RIM_COLOUR = CASING_COLOUR

/** The tick of a raised wedge: the wedge colour at 100 %, 3 px, with the arrowhead at its end in the same colour
 *  rimmed 1 px dark; one above the raised wedge's top polygon (10 + 2), whatever order the shapes were made in. */
export function tickStyle(colour: string) {
  return {
    strokeColor: colour, strokeOpacity: 1, strokeWeight: 3, zIndex: RAISED.zIndex + 3,
    icons: [{
      icon: { path: ARROWHEAD, fillColor: colour, fillOpacity: 1, strokeColor: TICK_RIM_COLOUR, strokeOpacity: 1, strokeWeight: 1, scale: 3 },
      offset: '100%',
    }],
  }
}
